import {
  ArcRotateCamera,
  Color3,
  Color4,
  DirectionalLight,
  Engine,
  HemisphericLight,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  Vector3,
} from '@babylonjs/core';

export interface ArenaScene {
  engine: Engine;
  scene: Scene;
  camera: ArcRotateCamera;
  dispose: () => void;
}

/** Shared scene boilerplate: engine, dark clear color, camera, lights, resize handling. */
export function createArenaScene(canvas: HTMLCanvasElement, cameraRadius = 2.6): ArenaScene {
  const engine = new Engine(canvas, true, { stencil: true });
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.016, 0.043, 0.1, 1);

  const camera = new ArcRotateCamera('cam', -Math.PI / 2, 0.95, cameraRadius, Vector3.Zero(), scene);
  camera.lowerRadiusLimit = 0.8;
  camera.upperRadiusLimit = 8;
  camera.upperBetaLimit = 1.45;
  camera.wheelDeltaPercentage = 0.01;
  camera.attachControl(canvas, true);

  const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  hemi.intensity = 0.65;
  hemi.groundColor = new Color3(0.05, 0.1, 0.2);
  const dir = new DirectionalLight('dir', new Vector3(-0.4, -1, 0.3), scene);
  dir.intensity = 0.7;

  const onResize = () => engine.resize();
  window.addEventListener('resize', onResize);

  engine.runRenderLoop(() => scene.render());

  return {
    engine,
    scene,
    camera,
    dispose: () => {
      window.removeEventListener('resize', onResize);
      scene.dispose();
      engine.dispose();
    },
  };
}

export function mat(scene: Scene, name: string, color: Color3, emissive = 0): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = color;
  m.specularColor = new Color3(0.08, 0.08, 0.08);
  if (emissive > 0) m.emissiveColor = color.scale(emissive);
  return m;
}

export interface RobotMeshOptions {
  color: Color3;
  width?: number;
  length?: number;
  height?: number;
}

/** Box chassis + visual wheels + glowing nose marker. Front faces +Z. */
export function createRobotMesh(scene: Scene, name: string, opts: RobotMeshOptions): Mesh {
  const w = opts.width ?? 0.18;
  const l = opts.length ?? 0.2;
  const h = opts.height ?? 0.09;

  const chassis = MeshBuilder.CreateBox(name, { width: w, depth: l, height: h }, scene);
  chassis.material = mat(scene, `${name}-mat`, opts.color, 0.25);

  const wheelMat = mat(scene, `${name}-wheel`, new Color3(0.12, 0.12, 0.14));
  const wheelPositions: [number, number][] = [
    [-w / 2 - 0.012, l * 0.28],
    [w / 2 + 0.012, l * 0.28],
    [-w / 2 - 0.012, -l * 0.28],
    [w / 2 + 0.012, -l * 0.28],
  ];
  wheelPositions.forEach(([x, z], i) => {
    const wheel = MeshBuilder.CreateCylinder(`${name}-w${i}`, { diameter: h * 0.95, height: 0.02 }, scene);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, -h * 0.15, z);
    wheel.material = wheelMat;
    wheel.parent = chassis;
  });

  const nose = MeshBuilder.CreateBox(`${name}-nose`, { width: w * 0.5, depth: 0.015, height: h * 0.4 }, scene);
  nose.position.set(0, h * 0.1, l / 2 + 0.008);
  nose.material = mat(scene, `${name}-nose-mat`, new Color3(1, 1, 1), 0.8);
  nose.parent = chassis;

  return chassis;
}

/** Signed yaw angle (radians) from `forward` to the direction of `to` on the XZ plane. */
export function signedAngleTo(forward: Vector3, to: Vector3): number {
  const f = new Vector3(forward.x, 0, forward.z).normalize();
  const t = new Vector3(to.x, 0, to.z).normalize();
  const cross = f.x * t.z - f.z * t.x;
  const dot = f.x * t.x + f.z * t.z;
  return Math.atan2(-cross, dot);
}
